import React, { useEffect, useMemo, useRef, useState } from 'react';
import socket from '../lib/socket';
import { getCategories, getRound } from '../lib/singingApi';
import './singing.css';

const LADDER = [300000,150000,100000,60000,30000,20000,12000,9000,6000,3000];

export default function SingingFront(){
  const [score, setScore] = useState(0);
  const [categories, setCategories] = useState([]);
  const [currentCat, setCurrentCat] = useState(null);
  const [currentPick, setCurrentPick] = useState(null); // { pickId, song:{...} }
  const [liveText, setLiveText] = useState('');         // 後台即時輸入的原文
  const [resultMsg, setResultMsg] = useState('');       // "答對了" / "錯了"
  const videoRef = useRef(null);

  // 讀初始資料
  useEffect(() => {
    (async () => {
      try {
        const cats = await getCategories();
        setCategories(cats?.categories || []);
      } catch(e){ console.error('cats', e); }

      try {
        const r = await getRound();
        const round = r?.round;
        if (round) setScore(Number(round.SCORE || 0));
      } catch(e){ console.error('round', e); }
    })();
  }, []);

  // socket 事件
  useEffect(() => {
    const onRound = ({ type, round }) => {
      if (type === 'round_started') {
        setScore(Number(round?.SCORE || 0));
        setCurrentCat(null);
        setCurrentPick(null);
        setLiveText('');
        setResultMsg('');
      }
    };
    const onCat = ({ categoryId, categoryName, songs, pickedSongIds = [] }) => {
      setCurrentCat({ id: categoryId, name: categoryName, songs, pickedSongIds });
      setCurrentPick(null);
      setLiveText('');
      setResultMsg('');
    };
    const onPlay = ({ pickId, song }) => {
      setCurrentPick({ pickId, song });
      setLiveText('');
      setResultMsg('');
      // 自動播放影片（若有）
      setTimeout(() => {
        try { videoRef.current?.play?.(); } catch {}
      }, 100);
    };
    const onMask = ({ masked }) => {
      // 伺服器傳來的是「原文」→ 直接顯示
      setLiveText(masked || '');
    };
    const onResult = ({ result, answer, score }) => {
      setScore(Number(score || 0));
      setResultMsg(result === 'pass' ? '答對了' : '錯了');
      // 若要揭示正解可改為：setLiveText(answer || '');
    };
    const onBackHome = () => {
      try { videoRef.current?.pause?.(); } catch {}
      setCurrentPick(null);
      setCurrentCat(null);
      setLiveText('');
      setResultMsg('');
    };

    socket.on('singing_game:state', onRound);
    socket.on('singing_game:category', onCat);
    socket.on('singing_game:play', onPlay);
    socket.on('singing_game:mask', onMask);
    socket.on('singing_game:result', onResult);
    socket.on('singing_game:back_home', onBackHome);
    return () => {
      socket.off('singing_game:state', onRound);
      socket.off('singing_game:category', onCat);
      socket.off('singing_game:play', onPlay);
      socket.off('singing_game:mask', onMask);
      socket.off('singing_game:result', onResult);
      socket.off('singing_game:back_home', onBackHome);
    };
  }, []);

  const activeStep = useMemo(() => {
    const idx = LADDER.findIndex(v => v <= score);
    return idx === -1 ? LADDER.length : idx;
  }, [score]);

  return (
    <div className="sg-wrap">
      {/* 左側獎金階梯 */}
      <div className="ladder">
        <div className="ladder-title">獎金累積</div>
        {LADDER.map((amt, i) => {
          const hl = i === activeStep;
          return (
            <div key={amt} className={`ladder-step ${hl ? 'active': ''}`}>
              <span>NT${amt.toLocaleString()}</span>
            </div>
          );
        })}
      </div>

      {/* 右側主區 */}
      <div className="stage">
        {/* 主題頁 */}
        {!currentCat && !currentPick && (
          <div className="topic-panel">
            <div className="panel-title">30萬大驚喜</div>
            <div className="topics">
              {categories.map((c) => (
                <div key={c.ID} className={`topic-pill color-${(c.ID%6)+1}`}>
                  {c.NAME}
                </div>
              ))}
            </div>
            <div className="panel-hint">（請口頭選一個主題，後台會幫你點）</div>
          </div>
        )}

        {/* 歌單頁（前台只顯示，不可點） */}
        {currentCat && !currentPick && (
          <div className="songlist-panel">
            <div className="song-tiles">
              {(currentCat.songs || []).map((s) => {
                const used = (currentCat.pickedSongIds || []).includes(s.id);
                return (
                  <div key={s.id} className={`song-tile ${used ? 'used':''}`}>
                    <div className="song-title">{s.title || '（未命名）'}</div>
                    <div className="song-meta">
                      {s.artist ? s.artist : '未知歌手'}
                      {s.year ? ` ・ ${s.year}` : ''}
                    </div>
                    {used && <div className="used-mask">已挑過</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 播放 + 即時字幕（上下排列，不重疊） */}
        {currentPick && (
          <div className="play-panel">
            {/* 置頂訊息橫幅（即時文字 + 判定徽章） */}
            <div className="mask-bar">
              <div className="mask-text">{liveText || '\u00A0'}</div>
              {resultMsg && (
                <div className={`result-chip ${resultMsg === '答對了' ? 'ok':'ng'}`}>
                  {resultMsg}
                </div>
              )}
            </div>

            {/* 影片：自動播放、放大 */}
            {(currentPick.song?.bgUrl || currentPick.song?.audioUrl) ? (
              <div className="player">
                <video
                  ref={videoRef}
                  src={currentPick.song?.bgUrl || currentPick.song?.audioUrl}
                  controls
                  playsInline
                  className="player-video"
                />
              </div>
            ) : (
              <div className="player empty">（本首歌曲沒有設定影片／音訊）</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
